import {useEffect, useRef, useState, type FormEvent} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {HelpIcon} from '@/components/help-icon'
import {FieldError} from '@/components/field-error'
import {RegistroShell} from '@/components/registro-shell'
import {useAuth} from '@/hooks/platform/use-auth'
import {
  useProbeCedula,
  useRegistroLink,
  useSubmitRegistro,
  type RegistroPage1Payload,
} from '@/hooks/empresa/identidad/use-registro'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {track} from '@/lib/telemetry'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, documentoNormalizado, type TipoDocumento} from '@/lib/documento'
import {validateCedula, validateEmail, validatePassword, validatePhone, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

type NewFormErrors = {name?: string; lastName?: string; email?: string; phone?: string; password?: string}

type Step = 'cedula' | 'existing' | 'new' | 'confirmar'

// Cómo se resolvió la identidad: solo para la telemetría del registro.
type Resultado = 'linked' | 'created'

/** Solo resuelve la identidad y navega derecho a mi/ficha, que retoma donde quedó el registro (KAI-219). */
export function RegistroPage() {
  const {t} = useTranslation()
  const {token} = useParams()
  const navigate = useNavigate()
  const {user, completeAuth} = useAuth()
  const {data: link, isLoading, isError} = useRegistroLink(token ?? '')
  const submit = useSubmitRegistro(token ?? '')
  const probe = useProbeCedula()

  const [step, setStep] = useState<Step>('cedula')
  const [cedula, setCedula] = useState('')
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('CC')
  const [cedulaError, setCedulaError] = useState<string | undefined>(undefined)
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [existingPasswordError, setExistingPasswordError] = useState<string | undefined>(undefined)
  const [newErrors, setNewErrors] = useState<NewFormErrors>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (link) track('registro_link_abierto')
  }, [link])

  // Con sesión abierta nunca se vincula solo: se pide el sí con la empresa a la vista (F15). El pestillo
  // evita que el `user` que estrena `completeAuth` en las ramas anónimas rebote a esta confirmación.
  const vinculacionResuelta = useRef(false)
  useEffect(() => {
    if (vinculacionResuelta.current || !user || !link) return
    vinculacionResuelta.current = true
    setStep('confirmar')
  }, [user, link])

  if (isLoading) {
    return (
      <RegistroShell>
        <Skeleton className="h-48 w-full" />
      </RegistroShell>
    )
  }

  if (isError || !link) {
    return (
      <RegistroShell>
        <div className="space-y-2 text-center" data-testid="registro-not-found">
          <h1 className="font-heading text-xl text-foreground">{t('registro.notFoundTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('registro.notFoundBody')}</p>
        </div>
      </RegistroShell>
    )
  }

  async function landOnFicha(resultado: Resultado) {
    track('registro_completado', {resultado})
    const {data} = await api.get<{items: {id: string}[]}>('/businesses', {params: {page_size: 1}})
    const id = data.items[0]?.id
    navigate(id ? `/businesses/${id}/mi/ficha` : '/', {replace: true})
  }

  async function landAfterAuth(accessToken: string, resultado: Resultado) {
    await completeAuth(accessToken)
    await landOnFicha(resultado)
  }

  async function onProbe(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const problem = validateCedula(cedula, tipoDocumento)
    setCedulaError(problem ?? undefined)
    if (problem) return
    const value = cedula.trim()
    try {
      const result = await probe.mutateAsync(value)
      const rama = result.status === 'existing_with_password' ? 'existing' : 'new'
      track('registro_cedula_ingresada', {rama})
      setStep(rama)
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  async function onAceptarVinculacion(cedula_propia: string) {
    setError(null)
    setBusy(true)
    try {
      await submit.mutateAsync({
        cedula: cedula_propia,
        tipo_documento: tipoDocumento,
        acepta_vinculacion: true,
      })
      await landOnFicha('linked')
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setBusy(false)
    }
  }

  async function onSubmitPayload(payload: RegistroPage1Payload, resultado: Resultado) {
    // Este envío ya es el acto deliberado: la sesión que cree no debe caer en la confirmación.
    vinculacionResuelta.current = true
    setError(null)
    setBusy(true)
    try {
      const result = await submit.mutateAsync(payload)
      if (result.access_token) {
        await landAfterAuth(result.access_token, resultado)
      } else {
        await landOnFicha(resultado)
      }
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setBusy(false)
    }
  }

  function onSubmitExisting(e: FormEvent) {
    e.preventDefault()
    const problem = validateRequired(password)
    setExistingPasswordError(problem ?? undefined)
    if (problem) return
    void onSubmitPayload(
      {
        cedula: cedula.trim(),
        tipo_documento: tipoDocumento,
        password,
        acepta_vinculacion: true,
      },
      'linked'
    )
  }

  function onSubmitNew(e: FormEvent) {
    e.preventDefault()
    const errors: NewFormErrors = {
      name: validateRequired(name) ?? undefined,
      lastName: validateRequired(lastName) ?? undefined,
      email: email.trim() ? (validateEmail(email) ?? undefined) : undefined,
      phone: phone.trim() ? (validatePhone(phone) ?? undefined) : undefined,
      password: validatePassword(password) ?? undefined,
    }
    setNewErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    void onSubmitPayload(
      {
        cedula: cedula.trim(),
        tipo_documento: tipoDocumento,
        name,
        last_name: lastName,
        email: email.trim() || null,
        phone: phone.trim() || null,
        password,
        acepta_vinculacion: true,
      },
      'created'
    )
  }

  if (step === 'confirmar' && user) {
    return (
      <RegistroShell eyebrow={link.business_name}>
        <div className="space-y-4" data-testid="registro-confirmar">
          <h1 className="font-heading text-2xl tracking-tight text-foreground">
            {t('registro.confirmarTitle', {empresa: link.business_name})}
          </h1>
          <p className="notice-amber rounded-lg px-3.5 py-3 text-sm" data-testid="registro-confirmar-datos">
            {t('registro.confirmarDatos', {empresa: link.business_name})}
          </p>
          {/* Fuera de la caja ámbar: dentro, su `text-muted-foreground` pisa el color del aviso. */}
          <PorQue
            label={t('registro.privacyLabelVer')}
            testId="registro-confirmar-porque"
            contenidoTestId="registro-confirmar-porque-detalle">
            {t('registro.privacyPorQue', {empresa: link.business_name})}
          </PorQue>
          {error && <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{error}</p>}
          <Button
            className="w-full"
            disabled={busy}
            onClick={() => void onAceptarVinculacion(user.cedula)}
            data-testid="registro-confirmar-aceptar">
            {t('registro.confirmarCta', {empresa: link.business_name})}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            disabled={busy}
            onClick={() => navigate('/', {replace: true})}
            data-testid="registro-confirmar-rechazar">
            {t('registro.confirmarRechazar')}
          </Button>
        </div>
      </RegistroShell>
    )
  }

  return (
    <RegistroShell eyebrow={link.business_name}>
      <h1 className="font-heading text-2xl tracking-tight text-foreground">{t('registro.title')}</h1>

      {step === 'cedula' && (
        <form onSubmit={onProbe} noValidate className="mt-6 space-y-4" data-testid="registro-form">
          <div className="space-y-2">
            <Label htmlFor="registro-cedula">{t('documento.label')}</Label>
            <div className="flex gap-2">
              <TipoDocumentoSelect value={tipoDocumento} onChange={setTipoDocumento} testId="registro-tipo-documento" />
              <Input
                id="registro-cedula"
                className="flex-1"
                inputMode={tipoDocumento === 'PA' ? 'text' : 'numeric'}
                maxLength={DOCUMENTO_MAX[tipoDocumento]}
                value={cedula}
                onChange={(e) => {
                  setCedula(documentoNormalizado(tipoDocumento, e.target.value))
                  setError(null)
                  setCedulaError(undefined)
                }}
                onBlur={() => setCedulaError(validateCedula(cedula, tipoDocumento) ?? undefined)}
                aria-invalid={!!cedulaError}
                required
              />
            </div>
            <FieldError error={cedulaError} />
            <Hint>{t(tipoDocumento === 'PA' ? 'documento.hintPA' : 'documento.hintCC')}</Hint>
          </div>
          {error && <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{error}</p>}
          <Button type="submit" className="w-full" disabled={probe.isPending || cedula.trim().length < 6}>
            {probe.isPending ? t('registro.checking') : t('registro.submitCedula')}
          </Button>
        </form>
      )}

      {step === 'existing' && (
        <form onSubmit={onSubmitExisting} noValidate className="mt-6 space-y-4" data-testid="registro-existing-form">
          <div className="notice-amber rounded-lg px-3.5 py-3">
            <p className="text-sm font-medium text-foreground">{t('registro.existingTitle')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="registro-existing-password">{t('registro.existingPasswordLabel')}</Label>
            <Input
              maxLength={tope('PASS')}
              id="registro-existing-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setExistingPasswordError(undefined)
              }}
              onBlur={() => setExistingPasswordError(validateRequired(password) ?? undefined)}
              aria-invalid={!!existingPasswordError}
              required
              autoFocus
            />
            <FieldError error={existingPasswordError} />
          </div>
          {error && <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy || password.length < 1}>
            {t('registro.existingSubmit')}
          </Button>
          <Link
            to="/reset-request"
            className="block text-center text-sm text-primary hover:underline"
            data-testid="registro-existing-forgot">
            {t('registro.existingForgot')}
          </Link>
        </form>
      )}

      {step === 'new' && (
        <form onSubmit={onSubmitNew} noValidate className="mt-6 space-y-4" data-testid="registro-new-form">
          <div className="space-y-2">
            <Label htmlFor="registro-name">{t('perfil.name')}</Label>
            <Input
              id="registro-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNewErrors((fe) => ({...fe, name: undefined}))
              }}
              onBlur={() => setNewErrors((fe) => ({...fe, name: validateRequired(name) ?? undefined}))}
              aria-invalid={!!newErrors.name}
              maxLength={tope('NOM')}
              required
            />
            <FieldError error={newErrors.name} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="registro-last-name">{t('perfil.lastName')}</Label>
            <Input
              id="registro-last-name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value)
                setNewErrors((fe) => ({...fe, lastName: undefined}))
              }}
              onBlur={() => setNewErrors((fe) => ({...fe, lastName: validateRequired(lastName) ?? undefined}))}
              aria-invalid={!!newErrors.lastName}
              maxLength={tope('NOM')}
              required
            />
            <FieldError error={newErrors.lastName} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="registro-email">{t('registro.emailOptional')}</Label>
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('registro.emailOptional')})}
                testId="registro-email-tip">
                {t('registro.emailOptionalTip')}
              </HelpIcon>
            </div>
            <Input
              id="registro-email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setNewErrors((fe) => ({...fe, email: undefined}))
              }}
              onBlur={() =>
                setNewErrors((fe) => ({...fe, email: email.trim() ? (validateEmail(email) ?? undefined) : undefined}))
              }
              aria-invalid={!!newErrors.email}
              maxLength={tope('MAIL')}
            />
            <FieldError error={newErrors.email} />
            {/* Sin correo, la contraseña es la única llave de la cuenta: por eso se lee sin ícono. */}
            {email.trim() ? (
              <Hint>{t('perfil.emailHint')}</Hint>
            ) : (
              <Hint testId="registro-sin-correo">{t('registro.sinCorreoHint')}</Hint>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="registro-phone">{t('registroEmpresa.phoneOptional')}</Label>
            <Input
              id="registro-phone"
              inputMode="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value)
                setNewErrors((fe) => ({...fe, phone: undefined}))
              }}
              onBlur={() =>
                setNewErrors((fe) => ({...fe, phone: phone.trim() ? (validatePhone(phone) ?? undefined) : undefined}))
              }
              aria-invalid={!!newErrors.phone}
              maxLength={tope('TEL')}
            />
            <FieldError error={newErrors.phone} />
            <Hint>{t('perfil.phoneHint')}</Hint>
          </div>
          <div className="space-y-2">
            <Label htmlFor="registro-password">{t('registro.passwordLabel')}</Label>
            <Input
              maxLength={tope('PASS')}
              id="registro-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setNewErrors((fe) => ({...fe, password: undefined}))
              }}
              onBlur={() => setNewErrors((fe) => ({...fe, password: validatePassword(password) ?? undefined}))}
              aria-invalid={!!newErrors.password}
              minLength={8}
              required
            />
            <FieldError error={newErrors.password} />
            <Hint>{t('common.passwordHint')}</Hint>
          </div>
          {error && <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy || !name || !lastName || password.length < 8}>
            {t('registro.submitNew')}
          </Button>
          <div className="space-y-1">
            <Hint testId="registro-privacy-notice">{t('registro.privacyNotice', {empresa: link.business_name})}</Hint>
            <PorQue
              label={t('registro.privacyLabel')}
              testId="registro-privacy-porque"
              contenidoTestId="registro-privacy-detalle">
              {t('registro.privacyPorQue', {empresa: link.business_name})}
            </PorQue>
          </div>
        </form>
      )}
    </RegistroShell>
  )
}
